import { AbstractControl } from '@angular/forms'
import { ConfigOption } from '@ngx-formly/core'
import { TranslateService } from '@ngx-translate/core'

import { CheckboxFieldTypeComponent } from './components/formly/types/checkbox-field-type/checkbox-field-type.component'
import { DateRangeFieldTypeComponent } from './components/formly/types/date-range-field-type/date-range-field-type.component'
import { InputFieldTypeComponent } from './components/formly/types/input-field/input-field-type.component'
import { NzInputFieldTypeComponent } from './components/formly/types/nz-input-field/nz-input-field.component'
import { RepeatFieldTypeComponent } from './components/formly/types/repeat-field-type/repeat-field-type.component'
import { SelectFieldTypeComponent } from './components/formly/types/select-field-type/select-field-type.component'
import { UploadFieldTypeComponent } from './components/formly/types/upload-field-type/upload-field-type.component'
import { CheckboxFieldWrapperComponent } from './components/formly/wrapper/checkbox-field-wrapper/checkbox-field-wrapper.component'
import { GroupFieldWrapperComponent } from './components/formly/wrapper/group-field-wrapper/group-field-wrapper.component'
import { InputFieldWrapperComponent } from './components/formly/wrapper/input-field-wrapper/input-field-wrapper.component'
import { NzFormFieldWrapperComponent } from './components/formly/wrapper/nz-form-field-wrapper/nz-form-field-wrapper.component'
import { TranslateExtension } from './formly-translate.extension'

export const formlyConfig: ConfigOption = {
  types: [
    {
      name: 'iconInput',
      component: InputFieldTypeComponent,
      wrappers: ['inputWrapper'],
    },
    {
      name: 'input',
      component: NzInputFieldTypeComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'code',
      component: InputFieldTypeComponent,
      wrappers: ['inputWrapper'],
    },
    {
      name: 'email',
      component: InputFieldTypeComponent,
      wrappers: ['inputWrapper'],
    },
    {
      name: 'password',
      component: InputFieldTypeComponent,
      wrappers: ['inputWrapper'],
    },
    {
      name: 'checkbox',
      component: CheckboxFieldTypeComponent,
      wrappers: ['checkboxWrapper'],
    },
    {
      name: 'file',
      component: UploadFieldTypeComponent,
    },
    {
      name: 'select',
      component: SelectFieldTypeComponent,
    },
    {
      name: 'date-range',
      component: DateRangeFieldTypeComponent,
    },
    {
      name: 'repeat',
      component: RepeatFieldTypeComponent,
    },
  ],
  wrappers: [
    {
      name: 'inputWrapper',
      component: InputFieldWrapperComponent,
    },
    {
      name: 'checkboxWrapper',
      component: CheckboxFieldWrapperComponent,
    },
    {
      name: 'groupWrapper',
      component: GroupFieldWrapperComponent,
    },
    {
      name: 'form-field', // extend default wrapper
      component: NzFormFieldWrapperComponent,
    },
  ],
  presets: [
    {
      name: 'email',
      config: {
        key: 'email',
        type: 'email',
        props: {
          placeholder: 'pages.login.email.placeholder',
          label: 'pages.login.email.label',
          pattern: String.raw`[^@\s]+@[^@\s]+\.[^@\s]+`,
        },
        validation: {
          messages: {
            pattern: 'validation.email.notAnEmail',
            required: 'validation.email.required',
          },
        },
      },
    },
    {
      name: 'code',
      config: {
        type: 'code',
        props: {
          pattern: /^\d{6}$/,
        },
        validation: {
          messages: {
            pattern: 'validation.confirmationCode.pattern',
            required: 'validation.confirmationCode.required',
          },
        },
      },
    },
    {
      name: 'password',
      config: {
        key: 'password',
        type: 'password',
        props: {
          required: true,
          label: 'pages.login.password.label',
          placeholder: 'pages.login.password.placeholder',
        },
        validation: {
          messages: {
            required: 'validation.password.required',
            notAuthorized: 'validation.notAuthorized',
            pattern: 'validation.password.invalid',
            fieldMatch: 'validation.password.noMatch',
            minlength: 'validation.password.minLength',
            maxlength: 'validation.password.maxLength',
          },
        },
      },
    },
    {
      name: 'passwordValidation',
      config: {
        key: 'passwordValidation',
        type: 'password',
        props: {
          required: true,
          label: 'pages.login.password.label',
          placeholder: 'pages.login.password.placeholder',
          minLength: 12,
          maxLength: 256,
        },
        validation: {
          messages: {
            required: 'validation.password.required',
            notAuthorized: 'validation.notAuthorized',
            pattern: 'validation.password.invalid',
            fieldMatch: 'validation.password.noMatch',
            minlength: 'validation.password.minLength',
            maxlength: 'validation.password.maxLength',
            uppercase: 'validation.password.uppercase',
            lowercase: 'validation.password.lowercase',
            number: 'validation.password.number',
            specialCharacter: 'validation.password.specialCharacter',
            noLeadingOrTrailingSpace:
              'validation.password.noLeadingOrTrailingSpace',
          },
        },
        validators: {
          uppercase: {
            expression: (control: AbstractControl) =>
              /[A-Z]/.test(control.value),
          },
          lowercase: {
            expression: (control: AbstractControl) =>
              /[a-z]/.test(control.value),
          },
          number: {
            expression: (control: AbstractControl) => /\d/.test(control.value),
          },
          specialCharacter: {
            expression: (control: AbstractControl) =>
              /[$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]/.test(control.value),
          },
          noLeadingOrTrailingSpace: {
            expression: (control: AbstractControl) =>
              control.value ? control.value.trim() === control.value : false,
          },
        },
      },
    },
    {
      name: 'select',
      config: {
        type: 'select',
        wrappers: ['form-field'],
        props: {
          hideRequiredMarker: true,
        },
      },
    },
  ],
  validators: [
    {
      name: 'number',
      validation: (control) =>
        !control.value || /^\d+$/.test(control.value) ? { number: true } : null,
    },
  ],
  validationMessages: [
    {
      name: 'required',
      message: 'validation.required',
    },
    {
      name: 'number',
      message: 'validation.number',
    },
  ],
}

export function registerTranslateExtension(
  translateService: TranslateService
): ConfigOption {
  return {
    extensions: [
      {
        name: 'autoTranslateAttributes',
        extension: new TranslateExtension(translateService),
      },
    ],
  }
}
